import { useState, useEffect } from 'react'
import { X } from '@/components/icons'
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { Button } from './ui/button'
import { Checkbox } from './ui/checkbox'
import { CrewMemberPickerDrawer } from './CrewMemberPickerDrawer'
import { DutyStatus } from '../../../api/src/shared'
import { useCrewGetAll } from '../lib/hooks/crew'
import { formatDutyStatus } from '../utils/statusColors'
import { PrntrCard, PrntrInput, PrntrPickerRow } from './ui/prntr-input'

export interface RosterFilters {
  crewMemberIds: string[]
  dutyStatuses: DutyStatus[]
  aircraftIds: string[]
  showAll: boolean
}

interface RosterFilterDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  filters: RosterFilters
  onFiltersChange: (filters: RosterFilters) => void
}

const DUTY_STATUS_OPTIONS = [
  DutyStatus.OnDuty,
  DutyStatus.OffDuty,
  DutyStatus.Standby,
  DutyStatus.Training,
  DutyStatus.Positioning,
]

export function RosterFilterDrawer({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
}: RosterFilterDrawerProps) {
  // Local state for editing filters before applying
  const [localFilters, setLocalFilters] = useState<RosterFilters>(filters)

  // Sub-drawer states
  const [showCrewPicker, setShowCrewPicker] = useState(false)
  const [showStatusPicker, setShowStatusPicker] = useState(false)

  // Fetch data for display names
  const { data: crewMembers = [] } = useCrewGetAll()

  // Reset local filters when drawer opens
  useEffect(() => {
    if (open) {
      setLocalFilters(filters)
    }
  }, [open, filters])

  const getCrewSummary = () => {
    if (localFilters.crewMemberIds.length === 0) return null
    const names = localFilters.crewMemberIds.map((id) => {
      const crew = crewMembers.find(c => c.id === id)
      if (!crew) return 'Unknown'
      return crew.firstName && crew.lastName
        ? `${crew.firstName} ${crew.lastName}`
        : crew.name || 'Unknown'
    })
    if (names.length <= 2) return names.join(', ')
    return `${names.length} crew members`
  }

  const getStatusSummary = () => {
    if (localFilters.dutyStatuses.length === 0) return null
    if (localFilters.dutyStatuses.length <= 2)
      return localFilters.dutyStatuses.map(s => formatDutyStatus(s)).join(', ')
    return `${localFilters.dutyStatuses.length} statuses`
  }

  const handleCancel = () => {
    setLocalFilters(filters)
    onOpenChange(false)
  }

  const handleApply = () => {
    onFiltersChange(localFilters)
    onOpenChange(false)
  }

  const handleReset = () => {
    // Aircraft is controlled by the schedule's aircraft card, not this drawer —
    // preserve the current aircraft selection when clearing crew + duty statuses.
    setLocalFilters(prev => ({
      ...prev,
      crewMemberIds: [],
      dutyStatuses: [],
      showAll: prev.aircraftIds.length === 0,
    }))
  }

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="bg-surface-L0 max-h-[85dvh]">
          <DrawerHeader className="flex items-center justify-between p-4">
            <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
              Filter
            </DrawerTitle>
            <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={() => onOpenChange(false)}>
              <X className="w-4 h-4" />
            </Button>
          </DrawerHeader>

          {/* Content */}
          <div className="flex-1 overflow-hidden px-4">
            <div className="flex flex-col gap-4 py-2">
              {/* Reset Button */}
              <button
                onClick={handleReset}
                className="self-start px-2 py-[3px] min-h-6 text-xs font-medium tracking-[0.18px] text-content-primary-bold border border-border-L3 rounded opacity-50 shadow-xs"
              >
                Reset
              </button>

              <PrntrCard>
                <PrntrPickerRow
                  label="Crew Members"
                  value={getCrewSummary()}
                  hideChevron
                  onClick={() => setShowCrewPicker(true)}
                  onClear={() =>
                    setLocalFilters(prev => ({
                      ...prev,
                      crewMemberIds: [],
                      showAll: prev.aircraftIds.length === 0 && prev.dutyStatuses.length === 0,
                    }))
                  }
                />
                <PrntrPickerRow
                  label="Duty Status"
                  value={getStatusSummary()}
                  hideChevron
                  onClick={() => setShowStatusPicker(true)}
                  onClear={() =>
                    setLocalFilters(prev => ({
                      ...prev,
                      dutyStatuses: [],
                      showAll: prev.aircraftIds.length === 0 && prev.crewMemberIds.length === 0,
                    }))
                  }
                />
              </PrntrCard>
            </div>
          </div>

          {/* Footer */}
          <DrawerFooter className="flex-row">
            <Button
              variant="secondary"
              size="mini"
              className="flex-1"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="mini"
              className="flex-1"
              onClick={handleApply}
            >
              Apply
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Crew Member Picker (multi-select) */}
      <CrewMemberPickerDrawer
        isOpen={showCrewPicker}
        onClose={() => setShowCrewPicker(false)}
        title="Filter by Crew"
        mode="multiple"
        schedulableOnly
        selectedCrewIds={localFilters.crewMemberIds}
        onSelectMultiple={(crewIds) => {
          setLocalFilters(prev => ({
            ...prev,
            crewMemberIds: crewIds,
            showAll:
              crewIds.length === 0 &&
              prev.dutyStatuses.length === 0 &&
              prev.aircraftIds.length === 0,
          }))
        }}
      />

      {/* Duty Status Picker (multi-select) */}
      <DutyStatusPickerDrawer
        open={showStatusPicker}
        onOpenChange={setShowStatusPicker}
        selectedStatuses={localFilters.dutyStatuses}
        onApply={(statuses) => {
          setLocalFilters(prev => ({
            ...prev,
            dutyStatuses: statuses,
            showAll:
              statuses.length === 0 &&
              prev.crewMemberIds.length === 0 &&
              prev.aircraftIds.length === 0,
          }))
        }}
      />
    </>
  )
}

function DutyStatusPickerDrawer({
  open,
  onOpenChange,
  selectedStatuses,
  onApply,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedStatuses: DutyStatus[]
  onApply: (statuses: DutyStatus[]) => void
}) {
  const [localStatuses, setLocalStatuses] = useState<DutyStatus[]>(selectedStatuses)

  // Re-seed draft when the picker opens
  useEffect(() => {
    if (open) {
      setLocalStatuses(selectedStatuses)
    }
  }, [open, selectedStatuses])

  const toggleStatus = (status: DutyStatus) => {
    setLocalStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    )
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-surface-L0 max-h-[85dvh]">
        <DrawerHeader className="flex items-center justify-between p-4">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
            Filter by Duty Status
          </DrawerTitle>
          <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={() => onOpenChange(false)}>
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-4">
          <div className="flex flex-col gap-4 py-2">
            <PrntrCard>
              {DUTY_STATUS_OPTIONS.map(status => (
                <PrntrInput
                  key={status}
                  label={formatDutyStatus(status)}
                  onClick={() => toggleStatus(status)}
                >
                  {/* Row click is the single toggle source */}
                  <Checkbox
                    checked={localStatuses.includes(status)}
                    className="pointer-events-none"
                  />
                </PrntrInput>
              ))}
            </PrntrCard>
          </div>
        </div>

        <DrawerFooter className="flex-row">
          <Button
            variant="secondary"
            size="mini"
            className="flex-1"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="mini"
            className="flex-1"
            onClick={() => {
              onApply(localStatuses)
              onOpenChange(false)
            }}
          >
            Apply
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
